import { atom } from "jotai";
import { StaffService } from "../../services/staffandplant/staffService";

// Base staff atom to store all staff data
export const staffAtom = atom({
  projectStaff: [],
  orgStaff: [],
  removedStaffIds: [], // Added to store removed staff IDs
  loading: false,
  error: null,
});

// Derived atom for UI state
export const staffUIAtom = atom({
  activeTab: 0, // 0 for project staff, 1 for org staff
  searchQuery: "",
  selectedStaff: [],
  filteredStaff: [],
});


const processStaffData = async (staffList) => {
  if (!Array.isArray(staffList)) {
    return [];
  }

  for (const staff of staffList) {
    if (!staff.user || !staff.user.profile_data) continue;
    
    if (staff.user.profile_data.other_documents) {
      staff.user.profile_data.other_documents = staff.user.profile_data.other_documents.map(doc => ({
        ...doc,
        upload_id: doc.upload_id || doc.id,
        name: doc.name || "Document",
        type: doc.type || 1
      }));
    }
  }
  
  return staffList;
};

// Fetch project staff
export const fetchProjectStaffAtom = atom(null, async (get, set) => {
  set(staffAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    const staff = await StaffService.fetchProjectStaff();
    const processedStaff = await processStaffData(staff);

    set(staffAtom, (prev) => ({
      ...prev,
      projectStaff: processedStaff,
      loading: false,
    }));

    return processedStaff;
  } catch (error) {
    set(staffAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

export const fetchOrgStaffAtom = atom(null, async (get, set) => {
  set(staffAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    const staff = await StaffService.fetchOrgStaff();
    const processedStaff = await processStaffData(staff);

    set(staffAtom, (prev) => ({
      ...prev,
      orgStaff: processedStaff,
      loading: false,
    }));

    return processedStaff;
  } catch (error) {
    set(staffAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

export const fetchStaffDocumentUrlAtom = atom(null, async (get, set, docId) => {
  try {
    const fileUrl = await StaffService.getFileUrl(docId, true);
    return fileUrl;
  } catch (error) {
    console.error(`Error fetching document URL for ${docId}:`, error);
    return null;
  }
});

const refreshStaffLists = async (set) => {
  try {
    await Promise.all([
      set(fetchProjectStaffAtom),
      set(fetchOrgStaffAtom)
    ]);
  } catch (error) {
    console.error("Error refreshing staff lists:", error);
  }
};

export const createStaffAtom = atom(null, async (get, set, { staffData, isOrgOnly = false }) => {
  set(staffAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    const userData = staffData.user;
    const payload = {
      email: userData.email,
      name: `${userData.firstname || userData.name} ${userData.lastname || ""}`.trim(),
      profile_data: {
        last_name: userData.lastname,
        number: userData.profile_data.mobile_number,
        role_auth: userData.job_title,
        country_code: userData.profile_data.country_code,
        photo_id: userData.profile_data.photo_id,
        day: userData.profile_data.day,
        ...(userData.profile_data.photo_url ? { photo_url: userData.profile_data.photo_url } : {}),
        ...(userData.profile_data.profile_url ? { profile_url: userData.profile_data.profile_url } : {}),
        other_documents: [],
      },
    };

    // Process other documents
    if (userData.profile_data.other_documents && userData.profile_data.other_documents.length > 0) {
      userData.profile_data.other_documents.forEach((doc) => {
        if (doc.upload_id) {
          payload.profile_data.other_documents.push({
            type: doc.type || 1,
            upload_id: doc.upload_id,
            name: doc.name || "Document",
          });
        }
      });
    }

    const result = await StaffService.createStaff(payload, isOrgOnly);

    // Refresh staff lists
    await refreshStaffLists(set);

    return result;
  } catch (error) {
    set(staffAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

// Update existing staff member
export const updateStaffAtom = atom(
  null,
  async (get, set, { staffId, staffData }) => {
    set(staffAtom, (prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Transform form data to API format
      const userData = staffData.user;
      const payload = {
        email: userData.email,
        name: userData.firstname + " " + (userData.lastname || ""),
        profile_data: {
          last_name: userData.lastname,
          number: userData.profile_data.mobile_number,
          role_auth: userData.job_title,
          country_code: userData.profile_data.country_code,
          photo_id: userData.profile_data.photo_id,
          day: userData.profile_data.day,
          ...(userData.profile_data.photo_url ? { photo_url: userData.profile_data.photo_url } : {}),
          ...(userData.profile_data.profile_url ? { profile_url: userData.profile_data.profile_url } : {}),
          other_documents: [],
        },
      };

      // Process other documents
      if (userData.profile_data.other_documents && userData.profile_data.other_documents.length > 0) {
        userData.profile_data.other_documents.forEach((doc) => {
          if (doc.upload_id) {
            payload.profile_data.other_documents.push({
              type: doc.type || 1,
              upload_id: doc.upload_id,
              name: doc.name || "Document",
            });
          }
        });
      }

      const result = await StaffService.updateStaff(staffId, payload);

      // Refresh staff lists
      await refreshStaffLists(set);

      return result;
    } catch (error) {
      set(staffAtom, (prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
      throw error;
    }
  }
);

// Delete staff member
export const deleteStaffAtom = atom(null, async (get, set, { staffId, forceDelete = false }) => {
  set(staffAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    const result = await StaffService.deleteStaff(staffId, forceDelete);

    // Refresh staff lists
    await refreshStaffLists(set);

    return result;
  } catch (error) {
    set(staffAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

// Deallocate staff from project
export const deallocateStaffAtom = atom(null, async (get, set, { staffId, forceDelete = false }) => {
  set(staffAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {
    await StaffService.deallocateStaff(staffId, forceDelete);

    set(staffAtom, (prev) => ({
      ...prev,
      projectStaff: prev.projectStaff.filter((s) => {
        const currentId = s.id || s.user?.id;
        return currentId !== staffId;
      }),
      loading: false
    }));

    return { success: true };
  } catch (error) {
    set(staffAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

// Import staff to project
export const importStaffAtom = atom(null, async (get, set, staffIds) => {
  set(staffAtom, (prev) => ({ ...prev, loading: true, error: null }));

  try {

    const staffIdList = Array.isArray(staffIds) ? staffIds : [staffIds];
    set(staffAtom, (prev) => ({
      ...prev,
      removedStaffIds: prev.removedStaffIds.filter(id => !staffIdList.includes(id)),
    }));

    const result = await StaffService.importStaff(staffIds);

    // Refresh staff lists
    await refreshStaffLists(set);

    return result;
  } catch (error) {
    set(staffAtom, (prev) => ({
      ...prev,
      loading: false,
      error: error.message,
    }));
    throw error;
  }
});

// Upload files atom
export const uploadFilesAtom = atom(null, async (get, set, files) => {
  try {
    const results = await StaffService.uploadMultipleFiles(files, true);
    return results;
  } catch (error) {
    console.error("Error uploading files:", error);
    throw error;
  }
});

// Utility atom to get current staff based on active tab
export const currentStaffAtom = atom((get) => {
  const staffState = get(staffAtom);
  const uiState = get(staffUIAtom);

  const staff = uiState.activeTab === 0 ? staffState.projectStaff : staffState.orgStaff;

  // Apply search filter
  if (!uiState.searchQuery) return staff;

  const searchLower = uiState.searchQuery.toLowerCase();
  return staff.filter((staffMember) => {
    return (
      (staffMember.user?.name || "").toLowerCase().includes(searchLower) ||
      (staffMember.user?.email || "").toLowerCase().includes(searchLower) ||
      (staffMember.user?.job_title || "").toLowerCase().includes(searchLower)
    );
  });
});

export const fetchStaffAtom = fetchProjectStaffAtom;
export const fetchImportStaffAtom = fetchOrgStaffAtom;
export const addNewUserAtom = createStaffAtom;
export const deleteStaff = deleteStaffAtom;
export const deAllocateAtom = deallocateStaffAtom;
export const updateImportStaffsAtom = importStaffAtom;

export default staffAtom;